import { createRoot } from "react-dom/client";
import ErrorBoundary from "./components/ErrorBoundary.tsx";
import { HelmetProvider } from "react-helmet-async";
import { validateStoredSupabaseSession } from "./lib/auth/bootCleanup";
import { registerServiceWorker } from "./lib/pwa/registerSw";
import { installGlobalErrorHandlers } from "./lib/errorReporter";
import { initSentry } from "./lib/sentry";
import { initWebVitals } from "./lib/webVitals";
import "./index.css";

// Defer Sentry init off the critical path so it doesn't block first paint.
// Use requestIdleCallback when available, fall back to setTimeout(0).
const scheduleSentry = () => {
  try { initSentry(); } catch {}
};
if (typeof (window as any).requestIdleCallback === "function") {
  (window as any).requestIdleCallback(scheduleSentry, { timeout: 2000 });
} else {
  setTimeout(scheduleSentry, 0);
}

// Force light mode — remove any dark class
document.documentElement.classList.remove("dark");

// Boot cleanup: validate (and clear if needed) the Supabase session BEFORE
// the SDK initializes. Prevents zombie sessions and refresh-token timeouts
// that produced "incognito works, normal session hangs" behavior.
validateStoredSupabaseSession();

// Install global error handlers (window.onerror + unhandledrejection).
// Reports to error_logs only when analytics consent is granted.
installGlobalErrorHandlers();

// Schedule Web Vitals collection off the critical path.
// web-vitals observers buffer entries from page start, so late registration is fine.
const scheduleWebVitals = () => { try { initWebVitals(); } catch {} };
if (typeof (window as any).requestIdleCallback === "function") {
  (window as any).requestIdleCallback(scheduleWebVitals, { timeout: 3000 });
} else {
  setTimeout(scheduleWebVitals, 100);
}

const root = createRoot(document.getElementById("root")!);

// Load the app only AFTER boot cleanup has run. Static-importing App here would
// initialize the auth client before stale token cleanup, recreating the exact
// refresh-token race that can blank the Dashboard on hard reload.
void import("./App.tsx").then(({ default: App }) => {
  root.render(
    <ErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </ErrorBoundary>
  );

  // Register Service Worker only on production custom domain (guards inside).
  // Fire-and-forget: never blocks render.
  registerServiceWorker();
});
